/*
 * Nybroe order flow. Catering plan v1.0.0 sections 3.1, 3.3 and 3.4, corrections C11, C13 and C15,
 * plus review addition C16 (payment methods and delivery limits at the start of ordering).
 *
 *   <nyb-order-options>  cart page block: method, hall, pickup phone, postcode check, date, time, summary.
 *   <nyb-order-info>     product page info: rule sentence, earliest dates, delivery limits, payment, choice.
 *   window.NybOrder      shared helpers for both elements and for assets/nyb-checkout-guard.js: config and
 *                        cart fetches, the serialized save queue, the clock and the nyb:state event.
 *
 * Business rules live only in assets/nyb-order-rules.js (window.NybRules). The copy lives in
 * locales/*.json under nybroe.order and reaches this file through snippets/nyb-order-strings.liquid.
 *
 * Under node (no HTMLElement) the file only exports the pure text and reconcile helpers for theme-tests.
 */
(function () {
  'use strict';

  var hasWindow = typeof window !== 'undefined';
  var hasModule = typeof module !== 'undefined' && module.exports;
  var R = hasWindow && window.NybRules ? window.NybRules : null;
  if (!R && hasModule && typeof require === 'function') {
    R = require(process.env.NYB_RULES_PATH || './nyb-order-rules.js');
  }
  if (!R) return;

  var CLEAR_ON_CHANGE = ['Validerad', 'Tidskalla', 'Regelversion'];
  var DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  var DAY_SHORT = { mon: 'mån', tue: 'tis', wed: 'ons', thu: 'tor', fri: 'fre', sat: 'lör', sun: 'sön' };
  var DELIVERY_CODES = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre'];
  var DELIVERY_LONG = { 'Mån': 'måndag', 'Tis': 'tisdag', 'Ons': 'onsdag', 'Tor': 'torsdag', 'Fre': 'fredag' };

  /* ------------------------------------------------------------------ pure helpers */

  function str(v) {
    return v === null || v === undefined ? '' : String(v).trim();
  }

  function fill(template, params) {
    return String(template === null || template === undefined ? '' : template).replace(/\[([a-z_]+)\]/g, function (m, key) {
      return params && Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : m;
    });
  }

  function pickAttrs(source) {
    var out = {};
    R.ATTRIBUTE_KEYS.forEach(function (k) {
      out[k] = str(source ? source[k] : '');
    });
    return out;
  }

  function displayPostcode(value) {
    var s = String(value === null || value === undefined ? '' : value).replace(/\s+/g, '');
    return /^\d{5}$/.test(s) ? s.slice(0, 3) + ' ' + s.slice(3) : str(value);
  }

  function ucfirst(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  function lcfirst(s) {
    return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
  }

  /* A word joiner after each hyphen keeps "10:00-18:00" on one line at 375 px (review of 2026-09-18). Display only:
     values stored or compared elsewhere keep the plain hyphen. */
  function nobr(s) {
    return s ? s.replace(/-/g, '-⁠') : s;
  }

  function deliveryDays(S, config) {
    var codes = config.rules.delivery_weekdays || [];
    if (!config.deliveryHall || !config.rules.delivery_windows.length) return '';
    var days = DELIVERY_CODES.filter(function (c) { return codes.indexOf(c) >= 0; });
    if (!days.length) return '';
    if (days.length === DELIVERY_CODES.length) return S.days_weekdays;
    var names = days.map(function (c) { return DELIVERY_LONG[c]; });
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' ' + S.days_and + ' ' + names[names.length - 1];
  }

  var Text = {
    fill: fill,
    displayPostcode: displayPostcode,

    ruleSentence: function (S, config) {
      var facts = R.ruleFacts(config);
      return facts ? fill(S.rule_sentence, { tid: facts.cutoffTime, dag: facts.mondayOrderWeekday }) : '';
    },

    /*
     * Pre-launch review 2026-09-18 (distance selling: delivery restrictions at the start of ordering): the line names
     * the hall delivery leaves from and the window, from the first window's start to the last window's end
     * ("Leverans från Östermalmshallen vardagar kl. 11:00 till 17:00 inom Stockholms kommun. ...").
     */
    deliveryLimits: function (S, config) {
      var days = deliveryDays(S, config);
      if (!days) return S.delivery_limits_none;
      var windows = config.rules.delivery_windows;
      return fill(S.delivery_limits, {
        hall: config.deliveryHall.name || config.deliveryHall.location_name,
        dagar: days,
        fran: R.formatHM(windows[0].open),
        till: R.formatHM(windows[windows.length - 1].close),
        innanfor: config.zones.inside.price,
        utanfor: config.zones.outside.price
      });
    },

    /*
     * The pickup halls with their opening hours for the product info box (same review): "Avhämtning i
     * Östermalmshallen (mån till lör 09:30-20:00, sön stängt) och Hötorgshallen (...)." Empty when no hall offers
     * pickup, so the line stays hidden.
     */
    pickupHalls: function (S, config) {
      var halls = (config && config.halls ? config.halls : []).filter(function (h) { return h.pickup_enabled; });
      if (!halls.length) return '';
      var parts = halls.map(function (h) {
        return h.name + ' (' + nobr(lcfirst(Text.hoursSummary(S, h))) + ')';
      });
      var list = parts.length === 1
        ? parts[0]
        : parts.slice(0, -1).join(', ') + ' ' + S.days_and + ' ' + parts[parts.length - 1];
      return fill(S.info_pickup_halls, { hallar: list });
    },

    /* The postcode result names the delivery fee and says it is added in checkout (pre-launch review 2026-09-18). */
    zoneResult: function (S, zone) {
      return fill(S.zone_result, { zon: lcfirst(zone.label), pris: zone.price });
    },

    contactSooner: function (S, config) {
      var r = config && config.rules ? config.rules : {};
      if (!r.contact_email) return '';
      return fill(S.contact_sooner, {
        epost: r.contact_email,
        ring: r.contact_phone ? fill(S.contact_call, { telefon: r.contact_phone }) : ''
      });
    },

    contactLine: function (S, config) {
      var sooner = Text.contactSooner(S, config);
      return (sooner ? sooner + ' ' : '') + (S.contact_later || '');
    },

    /* "Mån till lör 09:30-20:00, sön stängt" */
    hoursSummary: function (S, hall) {
      var groups = [];
      DAY_ORDER.forEach(function (k) {
        var h = hall.hours[k];
        var label = h ? R.formatHM(h.open) + '-' + R.formatHM(h.close) : S.hours_closed;
        var last = groups[groups.length - 1];
        if (last && last.label === label) last.to = k;
        else groups.push({ from: k, to: k, label: label });
      });
      return ucfirst(groups.map(function (g) {
        var days = g.from === g.to ? DAY_SHORT[g.from] : DAY_SHORT[g.from] + ' ' + S.hours_through + ' ' + DAY_SHORT[g.to];
        return days + ' ' + g.label;
      }).join(', '));
    },

    choiceText: function (S, attrs) {
      var a = attrs;
      if (!R.isDateString(a.Datum) || !a.Tid) return '';
      var datum = R.formatDateLong(a.Datum);
      if (a.Metod === R.PICKUP && a.Hall) return fill(S.choice_pickup, { hall: a.Hall, datum: datum, tid: a.Tid });
      if (a.Metod === R.DELIVERY && a.Postnummer) {
        return fill(S.choice_delivery, { postnummer: displayPostcode(a.Postnummer), datum: datum, tid: a.Tid });
      }
      return '';
    },

    summary: function (S, attrs) {
      var choice = Text.choiceText(S, attrs);
      return choice ? fill(S.summary, { val: choice }) : '';
    },

    /*
     * Plan 3.3 step 5 wants the exact checkout labels here (Q58). Captured verbatim on 2026-09-17 in the
     * WIP preview checkout (sv-se): the two tabs are "Frakt" and "Hämta"; after an address with postcode
     * 114 39 the shipping method reads "Lokal leverans" (350,00 kr); under "Hämta" checkout preselects a
     * location by itself (it showed Nybroe Hötorgshallen), so the pickup hint asks the customer to check
     * the hall. Only the locale strings carry the labels.
     */
    checkoutHint: function (S, attrs) {
      if (attrs.Metod === R.PICKUP && attrs.Hall) return fill(S.checkout_hint_pickup, { hall: attrs.Hall });
      if (attrs.Metod === R.DELIVERY && attrs.Postnummer) {
        return fill(S.checkout_hint_delivery, { postnummer: displayPostcode(attrs.Postnummer) });
      }
      return '';
    },

    closedMessage: function (S, config) {
      if (config && config.ok && config.rules && config.rules.ordering_paused) return config.rules.paused_message;
      return S.closed;
    },

    cutoffMessage: function (S, config, dateStr, cutoff) {
      var base = fill(S.cutoff_passed, {
        datum: R.isDateString(dateStr) ? R.formatDateLong(dateStr) : str(dateStr),
        tid: config.rules.cutoff_time,
        bestallningsdag: cutoff && cutoff.orderDay ? R.formatDateLong(cutoff.orderDay) : ''
      });
      var sooner = Text.contactSooner(S, config);
      return sooner ? base + ' ' + sooner : base;
    },

    staleMessage: function (S, dateStr, extra) {
      var msg = fill(S.stale_date, { datum: R.isDateString(dateStr) ? R.formatDateShort(dateStr) : str(dateStr) });
      return extra ? msg + ' ' + extra : msg;
    },

    /*
     * The polite status line for closed dates (QA 2026-09-17, F2), from R.closedDateNotices: each entry's
     * customer_message, or "Stängt torsdag 24 december" when it has none. Every part ends as a sentence, so
     * several closures read as one line.
     */
    closedDatesLine: function (S, notices) {
      return (notices || []).map(function (n) {
        var text = str(n.message) || fill(S.closed_date_fallback, {
          veckodag: R.weekdayName(n.date),
          datum: R.formatDayMonth(n.date)
        });
        return /[.!?]$/.test(text) ? text : text + '.';
      }).join(' ');
    },

    /* context 'guard' (checkout click, or a date picked just now) or 'load' (revalidation of a saved choice). */
    errorMessage: function (S, config, error, context) {
      var p = (error && error.params) || {};
      var code = error ? error.code : '';
      switch (code) {
        case 'config':
        case 'paused':
          return Text.closedMessage(S, config);
        case 'method_missing':
        case 'method_invalid':
          return S.error_method;
        case 'hall_missing':
        case 'hall_invalid':
          return S.error_hall;
        case 'phone_missing':
          return S.error_phone_missing;
        case 'phone_invalid':
          return S.error_phone_invalid;
        case 'postcode_missing':
        case 'zone_mismatch':
          return S.error_postcode_missing;
        case 'postcode_not_served':
          return R.normalizePostcode(p.postcode)
            ? fill(S.error_postcode_not_served, { postnummer: displayPostcode(p.postcode) })
            : S.error_postcode_format;
        case 'date_missing':
          return S.error_date;
        case 'time_missing':
          return S.error_time;
        case 'time_invalid':
          return S.error_time_gone;
        default:
          break;
      }
      if (code === 'date_cutoff_passed' && context === 'guard' && config && config.ok) {
        return Text.cutoffMessage(S, config, p.date, p.cutoff);
      }
      if (code.indexOf('date_') === 0) return Text.staleMessage(S, p.date, p.message);
      return S.error_generic;
    }
  };

  /*
   * Bring saved attributes in line with the rules at nowMs, without touching choices that are merely missing:
   * derive Zon and Hall from a delivery postcode, clear whatever validate() marks as invalid, clear date keys
   * left over without a date, and rewrite Veckodag, Stopptid and StopptidEpoch for a valid date.
   * Returns {attrs, updates, staleError, timeGone}.
   */
  function reconcile(attrs, config, nowMs) {
    var a = pickAttrs(attrs);
    var updates = {};
    function set(k, v) {
      if (a[k] !== v) {
        a[k] = v;
        updates[k] = v;
      }
    }
    var out = { attrs: a, updates: updates, staleError: null, timeGone: false };
    if (!config || !config.ok || config.rules.ordering_paused) return out;

    if (a.Metod === R.DELIVERY && a.Postnummer) {
      var zone = R.zoneFor(a.Postnummer, config);
      if (zone) {
        set('Zon', zone.label);
        if (config.deliveryHall) set('Hall', config.deliveryHall.location_name);
      }
    }

    var oldDate = a.Datum;
    var res = R.validate(a, nowMs, config, { ignoreStamp: true });
    res.errors.forEach(function (e) {
      if (!out.staleError && e.key === 'Datum' && e.code !== 'date_missing' && oldDate) out.staleError = e;
      if (e.code === 'time_invalid') out.timeGone = true;
    });
    res.clear.forEach(function (k) { set(k, ''); });

    if (!a.Datum) {
      ['Veckodag', 'Tid', 'Stopptid', 'StopptidEpoch'].forEach(function (k) { set(k, ''); });
    } else if (res.derived) {
      set('Veckodag', res.derived.Veckodag);
      set('Stopptid', res.derived.Stopptid);
      set('StopptidEpoch', res.derived.StopptidEpoch);
    }
    return out;
  }

  /*
   * The test clock (?nyb_now=) starts at the given instant when the page loads and then runs at real speed, so a
   * click 30 seconds later is judged 30 seconds later, exactly like the server clock. Other sources pass through.
   */
  function advanceTestClock(resolved, deviceNowMs, pageLoadedAtMs) {
    if (!resolved || resolved.source !== 'test') return resolved;
    return { ms: resolved.ms + Math.max(0, deviceNowMs - pageLoadedAtMs), source: 'test' };
  }

  /*
   * Config fetch URL and options (QA 2026-09-17, F1). Shopify caches /cart?sections=nyb-order-config renders (QA saw
   * them up to about 80 s old, cache 'no-store' notwithstanding), and a checkout click 15 s after ordering_paused was
   * set true passed the guard. A query parameter unique to every fetch makes each request miss that cache: the
   * device time only makes it unique (it never decides a rule), seq separates fetches in the same millisecond on
   * this page, and token separates pages. cache 'no-store' keeps the browser cache out as well.
   * Still no "Accept: application/json" header (see fetchConfig).
   */
  var CONFIG_FRESH_PARAM = 'nyb_fresh';

  function configUrl(base, nowMs, seq, token) {
    return base + '?sections=nyb-order-config&' + CONFIG_FRESH_PARAM + '=' +
      Number(nowMs).toString(36) + '-' + Number(seq).toString(36) + '-' + String(token);
  }

  function configFetchInit() {
    return { cache: 'no-store', credentials: 'same-origin' };
  }

  if (hasModule && typeof HTMLElement === 'undefined') {
    module.exports = {
      Text: Text,
      reconcile: reconcile,
      pickAttrs: pickAttrs,
      advanceTestClock: advanceTestClock,
      configUrl: configUrl,
      configFetchInit: configFetchInit,
      CONFIG_FRESH_PARAM: CONFIG_FRESH_PARAM
    };
    return;
  }
  if (!hasWindow || typeof HTMLElement === 'undefined' || typeof customElements === 'undefined' || window.NybOrder) return;

  /* ------------------------------------------------------------------ browser: fetches, clock, queue */

  var FETCH_TIMEOUT_MS = 15000;
  var NOTICE_KEY = 'nyb_order_notice';

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function cartUpdateUrl() {
    return ((window.routes && window.routes.cart_update_url) || '/cart/update') + '.js';
  }

  function withTimeout(promise) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, FETCH_TIMEOUT_MS);
      promise.then(
        function (v) { clearTimeout(timer); resolve(v); },
        function (e) { clearTimeout(timer); reject(e); }
      );
    });
  }

  var configFetchSeq = 0;
  var PAGE_TOKEN = Math.random().toString(36).slice(2, 10) || '0';

  /*
   * Rules from the section fetched by name (C09). null, bad JSON or a missing entry all give config.ok false.
   * No "Accept: application/json" header here: with it, /cart?sections= answers with the cart JSON instead of
   * the sections map (found on the preview theme 2026-09-17), which would keep ordering closed.
   * Every call is a new request with its own URL (configUrl, F1), never a cached or remembered config: the cart
   * block, the product info box and the checkout guard (at click time) all come through here.
   */
  function fetchConfig() {
    configFetchSeq += 1;
    return withTimeout(
      fetch(configUrl(cartUrl(), Date.now(), configFetchSeq, PAGE_TOKEN), configFetchInit())
    ).then(function (res) {
      if (!res.ok) throw new Error('config_http_' + res.status);
      return res.json();
    }).then(function (json) {
      return R.loadConfigFromSectionsResponse(json);
    });
  }

  /* The cart plus the response Date header (server time, plan 3.2). */
  function fetchCart() {
    return withTimeout(
      fetch(cartUrl() + '.js', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
    ).then(function (res) {
      if (!res.ok) throw new Error('cart_http_' + res.status);
      var dateHeader = res.headers.get('Date');
      return res.json().then(function (cart) {
        return { cart: cart || {}, dateHeader: dateHeader };
      });
    });
  }

  /* ?nyb_now= is honoured only when the theme setting is on, and never on the published theme. */
  function readTestNow() {
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main') return null;
      var v = new URLSearchParams(window.location.search).get('nyb_now');
      return v ? v.replace(/ /g, '+') : null;
    } catch (e) {
      return null;
    }
  }

  var PAGE_LOADED_AT = Date.now();

  function resolveNow(dateHeader, config) {
    var device = Date.now();
    return advanceTestClock(R.resolveNow({
      dateHeader: dateHeader,
      deviceNowMs: device,
      testClock: !!(config && config.testClock),
      nybNow: readTestNow()
    }), device, PAGE_LOADED_AT);
  }

  function makeClock(dateHeader, config) {
    var device = Date.now();
    var resolved = resolveNow(dateHeader, config);
    var offset = resolved.ms - device;
    return {
      source: resolved.source,
      now: function () { return Date.now() + offset; }
    };
  }

  /* One serialized queue for every attribute write, so the guard can wait for the last one. */
  var queue = Promise.resolve();

  function saveAttributes(attributes) {
    var run = queue.then(function () {
      return withTimeout(
        fetch(cartUpdateUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        })
      ).then(function (res) {
        if (!res.ok) throw new Error('update_http_' + res.status);
        return res.json();
      });
    });
    queue = run.catch(function () {});
    return run;
  }

  var blocks = [];

  function flush() {
    blocks.forEach(function (block) {
      try {
        if (typeof block.flushPending === 'function') block.flushPending();
      } catch (e) {
        /* a failing flush must not stop the guard; the guard validates the server cart anyway */
      }
    });
    return queue;
  }

  function storeNotice(message) {
    try {
      window.sessionStorage.setItem(NOTICE_KEY, message);
    } catch (e) {
      /* storage unavailable: the message is simply not carried over */
    }
  }

  function takeNotice() {
    try {
      var m = window.sessionStorage.getItem(NOTICE_KEY);
      if (m !== null) window.sessionStorage.removeItem(NOTICE_KEY);
      return m;
    } catch (e) {
      return null;
    }
  }

  var lastState = { complete: false, valid: false };

  function publishState(state) {
    lastState = state;
    document.dispatchEvent(new CustomEvent('nyb:state', { detail: state }));
  }

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function readStrings(root) {
    return readJson(root.querySelector('script[data-nyb-strings]')) || {};
  }

  /*
   * Field errors go to role="alert" elements (plan Q91, found in QA round 2). The element is cleared at once and the
   * message is written in a later task, so the same message is announced again after a new check. A later write or
   * clear cancels a pending one.
   */
  function writeFieldError(box, message) {
    var text = message || '';
    var token = (box._nybErrorToken || 0) + 1;
    box._nybErrorToken = token;
    if (box.textContent !== '') box.textContent = '';
    if (!text) return;
    setTimeout(function () {
      if (box._nybErrorToken === token) box.textContent = text;
    }, 50);
  }

  function subscribeCartUpdate(callback) {
    if (typeof subscribe !== 'function') return null;
    var eventName = typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';
    return subscribe(eventName, function (event) {
      callback(event);
    });
  }

  function cartBlock() {
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i].isConnected && blocks[i].tagName === 'NYB-ORDER-OPTIONS') return blocks[i];
    }
    return null;
  }

  function strings() {
    var block = cartBlock();
    return block ? block.S : {};
  }

  function messageFor(error, config) {
    return Text.errorMessage(strings(), config, error, 'guard') || '';
  }

  /* ------------------------------------------------------------------ <nyb-order-options> (cart) */

  class NybOrderOptions extends HTMLElement {
    connectedCallback() {
      if (!this._nybInit) {
        /* Once per element: listeners are bound to its own children, so a re-attach must not bind again. */
        this._nybInit = true;
        this.S = readStrings(this);
        var initial = readJson(this.querySelector('script[data-nyb-initial]')) || {};
        this.attrs = pickAttrs(initial.attributes);
        this.itemCount = typeof initial.item_count === 'number' ? initial.item_count : 0;
        this.designMode = this.getAttribute('data-design-mode') === 'true';
        this.config = null;
        this.clock = null;
        this.ready = false;
        this.phoneTimer = null;
        this.postcodeTimer = null;
        this.postcodeDirty = false;
        this.postcodeResult = null;
        this.lastPhoneOk = null;
        this.lastClosedMessage = null;
        this.cacheElements();
        this.bindEvents();
        this.onPageShow = function (event) {
          if (event.persisted) this.load();
        }.bind(this);
      }
      if (blocks.indexOf(this) < 0) blocks.push(this);
      this.unsubscribe = subscribeCartUpdate(this.onCartUpdate.bind(this));
      window.addEventListener('pageshow', this.onPageShow);
      clearInterval(this.tick);
      this.tick = setInterval(function () {
        if (this.ready) this.afterSync(false);
      }.bind(this), 60000);
      this.load();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      window.removeEventListener('pageshow', this.onPageShow);
      clearInterval(this.tick);
      clearTimeout(this.cartUpdateTimer);
      var i = blocks.indexOf(this);
      if (i >= 0) blocks.splice(i, 1);
      this.publish(false);
    }

    cacheElements() {
      var q = this.querySelector.bind(this);
      this.el = {
        banner: q('[data-nyb-banner]'),
        rule: q('[data-nyb-rule]'),
        limits: q('[data-nyb-limits]'),
        status: q('[data-nyb-status]'),
        alert: q('[data-nyb-alert]'),
        steps: q('[data-nyb-steps]'),
        methodInputs: Array.prototype.slice.call(this.querySelectorAll('input[name="nyb-cart-method"]')),
        payHint: q('[data-nyb-pay-hint]'),
        hallStep: q('[data-nyb-step="hall"]'),
        halls: q('[data-nyb-halls]'),
        phoneStep: q('[data-nyb-step="phone"]'),
        phone: q('#nyb-cart-phone'),
        phoneError: q('[data-nyb-phone-error]'),
        postcodeStep: q('[data-nyb-step="postcode"]'),
        postcode: q('#nyb-cart-postcode'),
        postcodeCheck: q('[data-nyb-postcode-check]'),
        postcodeResult: q('[data-nyb-postcode-result]'),
        postcodeError: q('[data-nyb-postcode-error]'),
        dateStep: q('[data-nyb-step="date"]'),
        date: q('#nyb-cart-date'),
        closedDates: q('[data-nyb-closed-dates]'),
        noDates: q('[data-nyb-no-dates]'),
        contact: q('[data-nyb-contact]'),
        timeStep: q('[data-nyb-step="time"]'),
        time: q('#nyb-cart-time'),
        summary: q('[data-nyb-summary]'),
        hint: q('[data-nyb-checkout-hint]')
      };
    }

    bindEvents() {
      var self = this;
      var el = this.el;
      el.methodInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (input.checked) self.onMethod(input.value);
        });
      });
      el.halls.addEventListener('change', function (event) {
        var t = event.target;
        if (t && t.name === 'nyb-cart-hall' && t.checked) self.onHall(t.value);
      });
      el.phone.addEventListener('input', function () { self.onPhoneInput(); });
      el.phone.addEventListener('change', function () { self.savePhone(true); });
      el.postcode.addEventListener('input', function () { self.onPostcodeInput(); });
      el.postcode.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          self.checkPostcode(true);
        }
      });
      el.postcodeCheck.addEventListener('click', function () { self.checkPostcode(true); });
      el.date.addEventListener('change', function () { self.onDate(el.date.value); });
      el.time.addEventListener('change', function () { self.onTime(el.time.value); });
    }

    now() {
      return this.clock ? this.clock.now() : Date.now();
    }

    setStatus(text) {
      this.el.status.textContent = text || '';
    }

    /* Closed-date line (F2). Written only when it changes, so the minute tick does not announce it again. */
    setClosedDates(text) {
      var box = this.el.closedDates;
      if (box && box.textContent !== (text || '')) box.textContent = text || '';
    }

    showAlert(message, focus) {
      var box = this.el.alert;
      box.textContent = '';
      /* Set in a later task so a repeated message is announced again. */
      setTimeout(function () {
        box.textContent = message || '';
        if (focus && message) box.focus();
      }, 50);
    }

    clearAlert() {
      this.el.alert.textContent = '';
    }

    publish(forceValid) {
      var valid = forceValid === false ? false : this.isValid();
      publishState({ complete: valid, valid: valid, source: 'cart' });
    }

    load() {
      this.ready = false;
      this.el.steps.hidden = true;
      this.setStatus(this.S.loading);
      this.publish(false);
      var self = this;
      return Promise.all([fetchConfig(), fetchCart()])
        .then(function (results) {
          var config = results[0];
          var cartRes = results[1];
          self.config = config;
          self.clock = makeClock(cartRes.dateHeader, config);
          self.attrs = pickAttrs(cartRes.cart.attributes);
          self.itemCount = typeof cartRes.cart.item_count === 'number' ? cartRes.cart.item_count : 0;
          self.ready = true;
          self.setStatus('');
          self.afterSync(true);
        })
        .catch(function () {
          self.ready = false;
          self.setStatus('');
          self.showAlert(self.S.error_generic, false);
          self.publish(false);
        });
    }

    afterSync(fromLoad) {
      var c = this.config;
      if (!c || !c.ok || c.rules.ordering_paused) {
        this.renderClosed();
        return;
      }
      if (this.itemCount === 0) {
        var clear = R.emptyCartClear({ item_count: 0, attributes: this.attrs });
        if (clear) {
          Object.assign(this.attrs, clear);
          this.save(clear);
        }
        this.resetInputs();
        this.render();
        return;
      }
      var rec = reconcile(this.attrs, c, this.now());
      this.attrs = rec.attrs;
      if (Object.keys(rec.updates).length) this.save(rec.updates);
      this.render();
      var message = null;
      if (rec.staleError) message = Text.errorMessage(this.S, c, rec.staleError, 'load');
      else if (rec.timeGone) message = this.S.error_time_gone;
      var notice = fromLoad ? takeNotice() : null;
      if (message || notice) this.showAlert(message || notice, false);
    }

    /* After a guard rejection: the cart as the server has it (with the cleared keys applied). */
    applyServerCart(cart, config, dateHeader) {
      this.config = config;
      this.clock = makeClock(dateHeader, config);
      this.attrs = pickAttrs(cart.attributes);
      this.itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;
      this.ready = true;
      this.postcodeDirty = false;
      this.postcodeResult = null;
      if (document.activeElement !== this.el.phone) this.el.phone.value = this.attrs.Telefon;
      if (!config.ok || config.rules.ordering_paused) {
        this.renderClosed();
        return;
      }
      /* Tidy what the rejection left behind (date keys without a date, an old validation stamp); the guard shows
         its own message, so no stale notice here. */
      var rec = reconcile(this.attrs, config, this.now());
      var self = this;
      CLEAR_ON_CHANGE.forEach(function (k) {
        if (rec.attrs[k]) {
          rec.attrs[k] = '';
          rec.updates[k] = '';
        }
      });
      this.attrs = rec.attrs;
      if (Object.keys(rec.updates).length) self.save(rec.updates);
      this.render();
    }

    save(updates) {
      var self = this;
      return saveAttributes(updates).catch(function () {
        self.showAlert(self.S.error_generic, false);
        return fetchCart().then(function (res) {
          self.attrs = pickAttrs(res.cart.attributes);
          self.itemCount = res.cart.item_count;
          self.render();
        }).catch(function () {
          self.publish(false);
        });
      });
    }

    /* A choice made by the customer: apply locally, save, and void any earlier checkout stamp. */
    change(updates) {
      var self = this;
      CLEAR_ON_CHANGE.forEach(function (k) {
        if (self.attrs[k] && !Object.prototype.hasOwnProperty.call(updates, k)) updates[k] = '';
      });
      Object.keys(updates).forEach(function (k) { self.attrs[k] = updates[k]; });
      this.save(updates);
    }

    clearDateUpdates() {
      var u = {};
      R.DATE_KEYS.forEach(function (k) { u[k] = ''; });
      return u;
    }

    dateUpdatesFor(dateStr, method, locationName) {
      var st = R.dateStatus(dateStr, method, locationName, this.now(), this.config);
      if (!st.selectable) return this.clearDateUpdates();
      var u = { Veckodag: R.weekdayName(dateStr), Stopptid: st.cutoff.label, StopptidEpoch: String(st.cutoff.epoch) };
      if (this.attrs.Tid && st.times.indexOf(this.attrs.Tid) < 0) u.Tid = '';
      return u;
    }

    resetInputs() {
      this.el.phone.value = '';
      this.el.postcode.value = '';
      this.postcodeDirty = false;
      this.postcodeResult = null;
      this.setPhoneError('');
      this.setPostcodeResult(null);
    }

    onMethod(value) {
      var method = value === 'delivery' ? R.DELIVERY : R.PICKUP;
      if (this.attrs.Metod === method) return;
      var u = { Metod: method };
      ['Hall', 'Telefon', 'Postnummer', 'Zon'].concat(R.DATE_KEYS).forEach(function (k) { u[k] = ''; });
      this.resetInputs();
      this.clearAlert();
      this.change(u);
      this.render();
    }

    onHall(locationName) {
      if (this.attrs.Hall === locationName) return;
      var u = { Hall: locationName };
      if (this.attrs.Datum) Object.assign(u, this.dateUpdatesFor(this.attrs.Datum, R.PICKUP, locationName));
      this.clearAlert();
      this.change(u);
      this.render();
    }

    setPhoneError(message) {
      writeFieldError(this.el.phoneError, message);
      if (message) this.el.phone.setAttribute('aria-invalid', 'true');
      else this.el.phone.removeAttribute('aria-invalid');
    }

    onPhoneInput() {
      var self = this;
      this.setPhoneError('');
      clearTimeout(this.phoneTimer);
      this.phoneTimer = setTimeout(function () { self.savePhone(false); }, 600);
      var ok = R.phoneOk(this.el.phone.value);
      if (ok !== this.lastPhoneOk) {
        this.lastPhoneOk = ok;
        this.render();
      } else {
        this.publish();
      }
    }

    savePhone(showError) {
      clearTimeout(this.phoneTimer);
      this.phoneTimer = null;
      if (this.attrs.Metod !== R.PICKUP) return;
      var value = this.el.phone.value.trim();
      if (showError && value !== '' && !R.phoneOk(value)) this.setPhoneError(this.S.error_phone_invalid);
      if (value !== this.attrs.Telefon) this.change({ Telefon: value });
      this.render();
    }

    /* A zone result goes to the polite role="status" line, an error to the role="alert" line (plan Q91). */
    setPostcodeResult(result) {
      this.postcodeResult = result;
      var isError = !!(result && result.error);
      var okText = result && !isError ? result.text : '';
      if (this.el.postcodeResult.textContent !== okText) this.el.postcodeResult.textContent = okText;
      writeFieldError(this.el.postcodeError, isError ? result.text : '');
      if (isError) this.el.postcode.setAttribute('aria-invalid', 'true');
      else this.el.postcode.removeAttribute('aria-invalid');
    }

    onPostcodeInput() {
      var self = this;
      this.postcodeDirty = true;
      this.setPostcodeResult(null);
      clearTimeout(this.postcodeTimer);
      if (R.normalizePostcode(this.el.postcode.value)) {
        this.postcodeTimer = setTimeout(function () { self.checkPostcode(false); }, 700);
      }
      this.render();
    }

    checkPostcode(explicit) {
      clearTimeout(this.postcodeTimer);
      this.postcodeTimer = null;
      if (!this.ready || this.attrs.Metod !== R.DELIVERY) return;
      var c = this.config;
      var S = this.S;
      var raw = this.el.postcode.value;
      var pc = R.normalizePostcode(raw);
      var u;
      if (!pc && !explicit) return;
      this.postcodeDirty = false;
      if (!pc) {
        this.setPostcodeResult({ text: raw.trim() === '' ? S.error_postcode_missing : S.error_postcode_format, error: true });
        u = Object.assign({ Postnummer: '', Zon: '', Hall: '' }, this.clearDateUpdates());
      } else {
        var zone = R.zoneFor(pc, c);
        this.el.postcode.value = displayPostcode(pc);
        if (!zone) {
          this.setPostcodeResult({ text: fill(S.error_postcode_not_served, { postnummer: displayPostcode(pc) }), error: true });
          u = Object.assign({ Postnummer: '', Zon: '', Hall: '' }, this.clearDateUpdates());
        } else {
          this.setPostcodeResult({ text: Text.zoneResult(S, zone), error: false });
          u = { Postnummer: pc, Zon: zone.label, Hall: c.deliveryHall.location_name, Telefon: '' };
          if (this.attrs.Datum) Object.assign(u, this.dateUpdatesFor(this.attrs.Datum, R.DELIVERY, u.Hall));
        }
      }
      var diff = {};
      var self = this;
      Object.keys(u).forEach(function (k) {
        if (self.attrs[k] !== u[k]) diff[k] = u[k];
      });
      if (Object.keys(diff).length) this.change(diff);
      this.render();
    }

    dateHallFor(method) {
      if (method === R.DELIVERY) return this.config.deliveryHall ? this.config.deliveryHall.location_name : '';
      return this.attrs.Hall;
    }

    onDate(value) {
      var a = this.attrs;
      var u;
      if (!value) {
        u = this.clearDateUpdates();
      } else {
        var st = R.dateStatus(value, a.Metod, this.dateHallFor(a.Metod), this.now(), this.config);
        if (!st.selectable) {
          u = this.clearDateUpdates();
          this.showAlert(Text.errorMessage(this.S, this.config, {
            code: 'date_' + st.reason,
            key: 'Datum',
            params: { date: value, cutoff: st.cutoff || null, message: st.message || '' }
          }, 'guard'), false);
        } else {
          u = { Datum: value, Veckodag: R.weekdayName(value), Stopptid: st.cutoff.label, StopptidEpoch: String(st.cutoff.epoch) };
          if (a.Tid && st.times.indexOf(a.Tid) < 0) u.Tid = '';
          this.clearAlert();
        }
      }
      this.change(u);
      this.render();
    }

    onTime(value) {
      if (this.attrs.Tid === value) return;
      this.clearAlert();
      this.change({ Tid: value });
      this.render();
    }

    /* Called by the guard before it reads the server cart: save anything still being typed. */
    flushPending() {
      if (!this.ready) return;
      if (this.attrs.Metod === R.PICKUP && (this.phoneTimer || this.el.phone.value.trim() !== this.attrs.Telefon)) {
        this.savePhone(true);
      }
      if (this.attrs.Metod === R.DELIVERY && (this.postcodeDirty || this.postcodeTimer)) this.checkPostcode(true);
    }

    /* The attributes as the customer sees them right now (a phone number being typed counts). */
    viewAttrs() {
      var view = Object.assign({}, this.attrs);
      if (view.Metod === R.PICKUP) view.Telefon = this.el.phone.value.trim();
      return view;
    }

    isValid() {
      var c = this.config;
      if (!this.ready || !c || !c.ok || c.rules.ordering_paused || this.itemCount <= 0) return false;
      if (this.attrs.Metod === R.DELIVERY && this.postcodeDirty) return false;
      return R.validate(this.viewAttrs(), this.now(), c, { ignoreStamp: true }).ok;
    }

    fillSelect(select, placeholder, options, selected) {
      var signature = placeholder + '|' + options.map(function (o) { return o.value + '=' + o.label; }).join('|');
      if (select.getAttribute('data-nyb-signature') !== signature) {
        select.textContent = '';
        var first = document.createElement('option');
        first.value = '';
        first.textContent = placeholder;
        select.appendChild(first);
        options.forEach(function (o) {
          var opt = document.createElement('option');
          opt.value = o.value;
          opt.textContent = o.label;
          select.appendChild(opt);
        });
        select.setAttribute('data-nyb-signature', signature);
      }
      var has = options.some(function (o) { return o.value === selected; });
      select.value = has ? selected : '';
    }

    renderHalls() {
      var c = this.config;
      var S = this.S;
      var halls = c.halls.filter(function (h) { return h.pickup_enabled; });
      var signature = halls.map(function (h) { return h.location_name + '=' + Text.hoursSummary(S, h) + '=' + str(h.address); }).join('|');
      var box = this.el.halls;
      if (box.getAttribute('data-nyb-signature') !== signature) {
        box.textContent = '';
        halls.forEach(function (h, i) {
          var id = 'nyb-cart-hall-' + (i + 1);
          var label = document.createElement('label');
          label.className = 'nyb-order__choice';
          label.setAttribute('for', id);
          var input = document.createElement('input');
          input.type = 'radio';
          input.id = id;
          input.name = 'nyb-cart-hall';
          input.value = h.location_name;
          /* The hall's street address comes from the nybroe_hall metaobject. It is only rendered when it is
             filled in, so an empty field never shows an empty line to the customer.
             The radio is named by the span that holds only the hall name, and described by the address and hours
             lines. Named by the whole label, a screen reader read the address and hours twice (pre-launch review
             2026-09-18). */
          var addressText = str(h.address);
          input.setAttribute('aria-labelledby', id + '-name');
          input.setAttribute('aria-describedby', addressText ? id + '-address ' + id + '-hours' : id + '-hours');
          var text = document.createElement('span');
          text.className = 'nyb-order__choice-text';
          var nameSpan = document.createElement('span');
          nameSpan.className = 'nyb-order__choice-name';
          nameSpan.id = id + '-name';
          nameSpan.textContent = h.name;
          text.appendChild(nameSpan);
          if (addressText) {
            var addr = document.createElement('span');
            addr.className = 'nyb-order__choice-sub';
            addr.id = id + '-address';
            addr.textContent = addressText;
            text.appendChild(addr);
          }
          var sub = document.createElement('span');
          sub.className = 'nyb-order__choice-sub';
          sub.id = id + '-hours';
          sub.textContent = nobr(Text.hoursSummary(S, h));
          text.appendChild(sub);
          label.appendChild(input);
          label.appendChild(text);
          box.appendChild(label);
        });
        box.setAttribute('data-nyb-signature', signature);
      }
      var current = this.attrs.Hall;
      Array.prototype.forEach.call(box.querySelectorAll('input[name="nyb-cart-hall"]'), function (input) {
        input.checked = input.value === current;
      });
    }

    renderClosed() {
      var c = this.config;
      var S = this.S;
      var el = this.el;
      el.steps.hidden = true;
      el.rule.hidden = true;
      el.limits.hidden = true;
      this.setStatus('');
      this.setClosedDates('');
      var message = c ? Text.closedMessage(S, c) : S.error_generic;
      /* Announce the closed state once, not on every minute tick. */
      if (message !== this.lastClosedMessage) {
        this.lastClosedMessage = message;
        this.showAlert(message, false);
      }
      if (this.designMode && c && !c.ok) {
        var codes = c.errors.map(function (e) {
          return e.code + (e.field ? ' ' + e.field : '') + (e.value !== undefined && e.value !== null ? ' (' + e.value + ')' : '');
        });
        el.banner.textContent = fill(S.editor_banner, { fel: codes.join(', ') });
        el.banner.hidden = false;
      }
      this.publish(false);
    }

    render() {
      if (!this.ready) return;
      var c = this.config;
      if (!c || !c.ok || c.rules.ordering_paused) {
        this.renderClosed();
        return;
      }
      var S = this.S;
      var el = this.el;
      var a = this.attrs;
      var nowMs = this.now();

      if (this.lastClosedMessage) {
        this.lastClosedMessage = null;
        this.clearAlert();
      }
      el.banner.hidden = true;
      if (this.designMode && c.warnings.length) {
        el.banner.textContent = c.warnings.map(function (w) { return w.code + (w.date ? ' ' + w.date : ''); }).join(', ');
        el.banner.hidden = false;
      }
      el.rule.textContent = Text.ruleSentence(S, c);
      el.rule.hidden = false;
      el.limits.textContent = Text.deliveryLimits(S, c);
      el.limits.hidden = false;
      el.steps.hidden = false;

      var isPickup = a.Metod === R.PICKUP;
      var isDelivery = a.Metod === R.DELIVERY;
      el.methodInputs.forEach(function (input) {
        input.checked = (input.value === 'pickup' && isPickup) || (input.value === 'delivery' && isDelivery);
      });
      el.payHint.hidden = !isDelivery;

      var step2ok = false;
      var dateHall = '';

      el.hallStep.hidden = !isPickup;
      var hall = null;
      if (isPickup) {
        this.renderHalls();
        hall = R.resolveHall(c, R.PICKUP, a.Hall);
      }
      var phoneRequired = c.rules.pickup_phone_required;
      el.phoneStep.hidden = !(isPickup && hall && phoneRequired);
      if (!el.phoneStep.hidden && document.activeElement !== el.phone && !this.phoneTimer) el.phone.value = a.Telefon;
      if (isPickup && hall) {
        step2ok = !phoneRequired || R.phoneOk(el.phone.value);
        dateHall = hall.location_name;
      }

      el.postcodeStep.hidden = !isDelivery;
      if (isDelivery) {
        var zone = a.Postnummer ? R.zoneFor(a.Postnummer, c) : null;
        if (zone && !this.postcodeDirty) {
          if (document.activeElement !== el.postcode) el.postcode.value = displayPostcode(a.Postnummer);
          if (!this.postcodeResult) this.setPostcodeResult({ text: Text.zoneResult(S, zone), error: false });
        }
        step2ok = !!zone && !this.postcodeDirty;
        dateHall = c.deliveryHall ? c.deliveryHall.location_name : '';
      }

      el.dateStep.hidden = !step2ok;
      var dates = step2ok ? R.listDates(a.Metod, dateHall, nowMs, c) : [];
      if (step2ok) {
        this.fillSelect(el.date, S.date_placeholder, dates.map(function (d) {
          return { value: d.date, label: d.labelShort };
        }), a.Datum);
        el.noDates.textContent = dates.length ? '' : S.no_dates;
        el.noDates.hidden = dates.length > 0;
        el.contact.textContent = Text.contactLine(S, c);
      }
      this.setClosedDates(step2ok ? Text.closedDatesLine(S, R.closedDateNotices(nowMs, c, a.Metod, dateHall)) : '');

      var entry = null;
      for (var i = 0; i < dates.length; i++) {
        if (dates[i].date === a.Datum) entry = dates[i];
      }
      el.timeStep.hidden = !entry;
      if (entry) {
        this.fillSelect(el.time, S.time_placeholder, entry.times.map(function (t) {
          return { value: t, label: t };
        }), a.Tid);
      }

      var valid = this.isValid();
      el.summary.textContent = valid ? Text.summary(S, a) : '';
      el.hint.textContent = valid ? Text.checkoutHint(S, a) : '';

      /* The checkout button sits in another block, and the customer could not tell which step was missing. The
         line under it names the next step instead of always asking for a day and a time (found in the customer
         walkthrough on 2026-09-17: the date and time selects do not exist until the phone number is valid). */
      var why = document.querySelector('[data-nyb-why]');
      if (why) {
        var reason = '';
        if (!valid) {
          if (a.Metod !== R.PICKUP && a.Metod !== R.DELIVERY) {
            reason = S.why_method;
          } else if (a.Metod === R.PICKUP && !hall) {
            reason = S.why_hall;
          } else if (a.Metod === R.PICKUP && phoneRequired && !R.phoneOk(el.phone.value)) {
            reason = S.why_phone;
          } else if (a.Metod === R.DELIVERY && !step2ok) {
            reason = S.why_postcode;
          } else if (!a.Datum) {
            reason = S.why_date;
          } else if (!a.Tid) {
            reason = S.why_time;
          }
        }
        why.textContent = reason || (valid ? '' : S.checkout_disabled_why);
      }
      this.lastPhoneOk = R.phoneOk(el.phone.value);
      this.publish();
    }

    onCartUpdate(event) {
      var self = this;
      var data = event && event.cartData;
      clearTimeout(this.cartUpdateTimer);
      this.cartUpdateTimer = setTimeout(function () {
        if (!self.ready) return;
        if (data && typeof data.item_count === 'number') {
          self.itemCount = data.item_count;
          self.afterSync(false);
          return;
        }
        fetchCart().then(function (res) {
          self.itemCount = typeof res.cart.item_count === 'number' ? res.cart.item_count : 0;
          self.afterSync(false);
        }).catch(function () {});
      }, 200);
      /* Nothing is returned: the theme's publish() waits for subscribers. */
    }
  }

  /* ------------------------------------------------------------------ <nyb-order-info> (product page) */

  class NybOrderInfo extends HTMLElement {
    connectedCallback() {
      if (!this._nybInit) {
        this._nybInit = true;
        this.S = readStrings(this);
        var q = this.querySelector.bind(this);
        this.el = {
          status: q('[data-nyb-status]'),
          closed: q('[data-nyb-closed]'),
          rule: q('[data-nyb-rule]'),
          pickup: q('[data-nyb-earliest-pickup]'),
          delivery: q('[data-nyb-earliest-delivery]'),
          pickupHalls: q('[data-nyb-pickup-halls]'),
          closedDates: q('[data-nyb-closed-dates]'),
          limits: q('[data-nyb-limits]'),
          choice: q('[data-nyb-choice]'),
          choiceText: q('[data-nyb-choice-text]'),
          choiceLink: q('[data-nyb-choice-link]')
        };
        this.config = null;
      }
      if (blocks.indexOf(this) < 0) blocks.push(this);
      this.unsubscribe = subscribeCartUpdate(this.onCartUpdate.bind(this));
      this.load();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      clearTimeout(this.cartUpdateTimer);
      var i = blocks.indexOf(this);
      if (i >= 0) blocks.splice(i, 1);
    }

    load() {
      var self = this;
      this.el.status.textContent = this.S.loading;
      Promise.all([fetchConfig(), fetchCart()])
        .then(function (results) {
          self.config = results[0];
          self.apply(results[1]);
        })
        .catch(function () {
          self.el.status.textContent = '';
          self.showClosed(self.S.error_generic);
        });
    }

    onCartUpdate() {
      var self = this;
      clearTimeout(this.cartUpdateTimer);
      this.cartUpdateTimer = setTimeout(function () {
        if (!self.config) return;
        fetchCart().then(function (res) { self.apply(res); }).catch(function () {});
      }, 300);
    }

    /* Closed-date line (F2). Written only when it changes, so cart updates do not announce it again. */
    setClosedDates(text) {
      var box = this.el.closedDates;
      if (box && box.textContent !== (text || '')) box.textContent = text || '';
    }

    showClosed(message) {
      var el = this.el;
      el.closed.textContent = message;
      el.closed.hidden = false;
      el.rule.hidden = true;
      el.pickup.hidden = true;
      el.delivery.hidden = true;
      if (el.pickupHalls) el.pickupHalls.hidden = true;
      el.limits.hidden = true;
      el.choice.hidden = true;
      this.setClosedDates('');
    }

    apply(cartRes) {
      var S = this.S;
      var c = this.config;
      var el = this.el;
      var cart = cartRes.cart || {};
      el.status.textContent = '';
      if (!c || !c.ok || c.rules.ordering_paused) {
        this.showClosed(Text.closedMessage(S, c));
        return;
      }
      el.closed.hidden = true;
      var nowMs = makeClock(cartRes.dateHeader, c).now();
      var attrs = pickAttrs(cart.attributes);

      if (cart.item_count === 0) {
        var clear = R.emptyCartClear(cart);
        if (clear) {
          saveAttributes(clear).catch(function () {});
          attrs = pickAttrs(clear);
        }
      } else {
        var rec = reconcile(attrs, c, nowMs);
        if (Object.keys(rec.updates).length) saveAttributes(rec.updates).catch(function () {});
        if (rec.staleError) storeNotice(Text.errorMessage(S, c, rec.staleError, 'load'));
        else if (rec.timeGone) storeNotice(S.error_time_gone);
        attrs = rec.attrs;
      }

      el.rule.textContent = Text.ruleSentence(S, c);
      el.rule.hidden = false;

      var pickup = R.earliestDate(R.PICKUP, nowMs, c);
      el.pickup.textContent = pickup ? fill(S.info_earliest_pickup, { datum: R.formatDateLong(pickup.date) }) : '';
      el.pickup.hidden = !pickup;

      var delivery = R.earliestDate(R.DELIVERY, nowMs, c);
      el.delivery.textContent = delivery ? fill(S.info_earliest_delivery, { datum: R.formatDateLong(delivery.date) }) : '';
      el.delivery.hidden = !delivery;

      if (!pickup && !delivery) {
        el.pickup.textContent = S.no_dates + ' ' + Text.contactLine(S, c);
        el.pickup.hidden = false;
      }

      /* Scope: the method and hall saved in the cart; nothing chosen yet means every method and hall (F2). */
      this.setClosedDates(Text.closedDatesLine(S, R.closedDateNotices(nowMs, c, attrs.Metod, attrs.Hall)));

      if (el.pickupHalls) {
        var hallsLine = Text.pickupHalls(S, c);
        el.pickupHalls.textContent = hallsLine;
        el.pickupHalls.hidden = !hallsLine;
      }

      el.limits.textContent = Text.deliveryLimits(S, c);
      el.limits.hidden = false;

      var valid = cart.item_count > 0 && R.validate(attrs, nowMs, c, { ignoreStamp: true }).ok;
      el.choiceText.textContent = valid ? fill(S.info_current_choice, { val: Text.choiceText(S, attrs) }) : S.info_choose_in_cart;
      el.choiceLink.hidden = !valid;
      el.choice.hidden = false;
    }
  }

  window.NybOrder = {
    VERSION: '1.0.0',
    Text: Text,
    reconcile: reconcile,
    pickAttrs: pickAttrs,
    fetchConfig: fetchConfig,
    fetchCart: fetchCart,
    resolveNow: resolveNow,
    save: saveAttributes,
    flush: flush,
    cartBlock: cartBlock,
    messageFor: messageFor,
    storeNotice: storeNotice,
    get savePromise() { return queue; },
    get lastState() { return lastState; }
  };

  if (!customElements.get('nyb-order-options')) customElements.define('nyb-order-options', NybOrderOptions);
  if (!customElements.get('nyb-order-info')) customElements.define('nyb-order-info', NybOrderInfo);
})();
